import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";

/**
 * LiveClock
 * Server-authoritative football match clock.
 *
 * Props:
 *   matchId        — required
 *   initialClock   — optional seed from parent (match-centre payload) to avoid an extra request
 *   pollSeconds    — server poll interval (default 5s). Only polls while running/paused/halftime.
 *   onClockUpdate  — optional callback(clock) whenever a server poll resolves.
 *   className      — wrapper classes.
 *
 * Behaviour:
 *   - Reads `clock_state`, `period_started_at` and `is_running` from the server.
 *   - Computes the displayed minute locally every 500ms using (now - period_started_at)
 *     plus a wall-clock skew correction so all viewers converge within the poll window.
 *   - Shows football-style minute: "12'", "45+2'", "67'", "HT", "FT", "PAUSED", etc.
 */
const LIVE_LIKE_STATES = new Set([
  "first_half",
  "half_time",
  "second_half",
  "extra_time_first",
  "extra_time_half",
  "extra_time_second",
  "paused",
  "shootout",
]);

const RUNNING_STATES = new Set(["first_half", "second_half", "extra_time_first", "extra_time_second"]);

const parseIso = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d) ? null : d;
};

const minuteFromSeconds = (elapsedSeconds, capMinutes, offsetMinutes = 0) => {
  if (elapsedSeconds < 0) elapsedSeconds = 0;
  const capSeconds = capMinutes * 60;
  if (elapsedSeconds <= capSeconds) {
    let minute = Math.floor(elapsedSeconds / 60) + 1;
    minute = Math.min(minute, capMinutes);
    return { minute: offsetMinutes + minute, added: null };
  }
  const extra = elapsedSeconds - capSeconds;
  const added = Math.floor(extra / 60) + 1;
  return { minute: offsetMinutes + capMinutes, added };
};

const resolveDisplay = (state, periodElapsedSeconds) => {
  switch (state) {
    case "first_half":
      return minuteFromSeconds(periodElapsedSeconds, 45, 0);
    case "half_time":
      return { minute: 45, added: null, label: "HT" };
    case "second_half":
      return minuteFromSeconds(periodElapsedSeconds, 45, 45);
    case "extra_time_first":
      return minuteFromSeconds(periodElapsedSeconds, 15, 90);
    case "extra_time_half":
      return { minute: 105, added: null, label: "ET HT" };
    case "extra_time_second":
      return minuteFromSeconds(periodElapsedSeconds, 15, 105);
    case "shootout":
      return { minute: null, added: null, label: "PEN" };
    case "full_time":
      return { minute: 90, added: null, label: "FT" };
    case "paused":
      return { minute: null, added: null, label: "PAUSED" };
    case "abandoned":
      return { minute: null, added: null, label: "ABD" };
    case "pre_match":
      return { minute: null, added: null, label: "SCHEDULED" };
    default:
      return { minute: null, added: null, label: (state || "SCHEDULED").toUpperCase() };
  }
};

export const formatClock = (state, minute, added) => {
  if (state === "half_time") return "HT";
  if (state === "full_time") return "FT";
  if (state === "paused") return "PAUSED";
  if (state === "abandoned") return "ABD";
  if (state === "shootout") return "PEN";
  if (state === "pre_match" || minute == null) return "";
  return added ? `${minute}+${added}'` : `${minute}'`;
};

const LiveClock = ({ matchId, initialClock, pollSeconds = 5, onClockUpdate, className = "" }) => {
  const [clock, setClock] = useState(initialClock || null);
  const [tick, setTick] = useState(0);
  const skewRef = useRef(0); // server_time - client_time, ms

  // Poll the server for authoritative clock state
  useEffect(() => {
    let cancelled = false;
    const fetchClock = async () => {
      try {
        const res = await axios.get(`${API}/matches/${matchId}/clock`);
        if (cancelled) return;
        const serverNow = new Date(res.data.server_time).getTime();
        skewRef.current = serverNow - Date.now();
        setClock(res.data);
        onClockUpdate?.(res.data);
      } catch (e) {
        // silent — the match-centre page will surface errors
      }
    };
    // Only poll if the clock looks "live-like"; still fetch once initially.
    fetchClock();
    const state = clock?.clock_state;
    const shouldPoll = !state || LIVE_LIKE_STATES.has(state);
    if (!shouldPoll) return () => { cancelled = true; };
    const iv = setInterval(fetchClock, Math.max(2, pollSeconds) * 1000);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, clock?.clock_state, pollSeconds]);

  // Local 500ms ticker — only when the clock is actively running
  useEffect(() => {
    if (!clock?.is_running) return;
    const iv = setInterval(() => setTick((t) => t + 1), 500);
    return () => clearInterval(iv);
  }, [clock?.is_running]);

  if (!clock) return null;

  // Compute current period elapsed using server-authoritative period_started_at + skew
  let periodElapsed = clock.period_elapsed_seconds || 0;
  if (clock.is_running && clock.period_started_at) {
    const started = parseIso(clock.period_started_at);
    if (started) {
      const nowMs = Date.now() + skewRef.current;
      periodElapsed = Math.max(0, Math.floor((nowMs - started.getTime()) / 1000));
    }
  }

  const displayState = clock.clock_state === "paused" ? (clock.prev_clock_state || "first_half") : clock.clock_state;
  const resolved = resolveDisplay(displayState, periodElapsed);
  // For paused state the resolved minute/added reflects the frozen state
  const display = clock.clock_state === "paused"
    ? { minute: resolved.minute, added: resolved.added, label: "PAUSED" }
    : resolved;

  const label = display.label;
  const text = display.minute != null
    ? (display.added ? `${display.minute}+${display.added}'` : `${display.minute}'`)
    : label;

  // Visual treatment
  const isLive = RUNNING_STATES.has(clock.clock_state);
  const isPaused = clock.clock_state === "paused" || clock.clock_state === "half_time" || clock.clock_state === "extra_time_half";
  const isFinal = clock.clock_state === "full_time" || clock.clock_state === "abandoned";

  return (
    <div className={`inline-flex items-center gap-2 ${className}`} data-testid="live-clock" data-clock-state={clock.clock_state}>
      {isLive && <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />}
      {isLive && <span className="text-red-500 font-bold uppercase text-xs tracking-widest">LIVE</span>}
      {isPaused && !isFinal && <span className="text-amber-400 font-bold uppercase text-xs tracking-widest">{label === "PAUSED" ? "PAUSED" : label}</span>}
      {isFinal && <span className="text-gray-300 font-bold uppercase text-xs tracking-widest">{label}</span>}
      {(display.minute != null || isLive) && (
        <span
          className="font-mono font-bold text-sm tracking-widest"
          data-testid="live-clock-minute"
        >
          {text}
        </span>
      )}
    </div>
  );
};

export default LiveClock;
